import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { OpportunityStatusAvailableDto } from '@api-proxies';
import { OpportunityStatuses } from '@models/enums/OpportunityStatuses';
import { StatusMap } from '@models/enums/StatusMap';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import { ColorType, icons, IconSanitizerService, StatusIndicatorComponent } from 'ngx-lib';
import { OpportunityBidStatusMenuComponent } from './opportunity-bid-status-menu/opportunity-bid-status-menu.component';
import { OpportunityStatusActionsMenuComponent } from './opportunity-status-actions-menu/opportunity-status-actions-menu.component';

export interface OpportunityStatusIndicatorConfig {
    status: OpportunityStatuses;
    availableStatus: OpportunityStatusAvailableDto[];
    hasLinkedBid: boolean;
    hasManualStatus: boolean;
    canDisqualified: boolean;
    showManualStatusOption: boolean;
    canEditOpportunity: boolean;
}

@Component({
    selector: 'app-opportunity-status-indicator',
    templateUrl: './opportunity-status-indicator.component.html',
    styleUrls: ['./opportunity-status-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        ...CDK_IMPORTS,
        StatusIndicatorComponent,
        OpportunityStatusActionsMenuComponent,
        OpportunityBidStatusMenuComponent
    ]
})
export class OpportunityStatusIndicatorComponent {
    private readonly iconSanitizer = inject(IconSanitizerService);

    @ViewChild('trigger')
    private trigger?: CdkOverlayOrigin;

    changeStatusEvent = output<OpportunityStatusAvailableDto>();

    config = input.required<OpportunityStatusIndicatorConfig>();

    opportunityStatuses = OpportunityStatuses;

    manualStatusClick = output<void>();

    disqualifyToggleClick = output<void>();

    readonly hasMenuActions = computed<boolean>(() => {
        const config = this.config();
        if (!(config?.canEditOpportunity ?? false)) {
            return false;
        }
        const statusValue = config?.status;
        const isUnqualifiedOrDisqualified =
            statusValue === OpportunityStatuses.Unqualified || statusValue === OpportunityStatuses.Disqualified;

        return (config?.showManualStatusOption ?? false) || !!isUnqualifiedOrDisqualified;
    });

    readonly hasAnyOptions = computed<boolean>(() => {
        const config = this.config();
        const availableCount = config?.availableStatus?.length ?? 0;
        if (config?.hasLinkedBid) {
            return availableCount > 0;
        }
        return availableCount > 0 || this.hasMenuActions();
    });

    isOverlayGroupsOpen = signal<boolean>(false);

    readonly overlayMinWidth = signal<number>(0);

    readonly currentIcon = computed<keyof typeof icons>(() => {
        const statusValue = this.config()?.status;
        const statusData = statusValue != null ? StatusMap[statusValue] : undefined;
        return statusData?.icon ?? 'edit';
    });

    readonly currentText = computed<string>(() => {
        const statusValue = this.config()?.status;
        const statusData = statusValue != null ? StatusMap[statusValue] : undefined;
        return statusData?.text ?? 'Unqualified';
    });

    readonly currentColorType = computed<ColorType>(() => {
        const statusValue = this.config()?.status;
        const statusData = statusValue != null ? StatusMap[statusValue] : undefined;
        return statusData?.colorType ?? 'idle';
    });

    readonly toggleArrowIcon = computed(() =>
        this.isOverlayGroupsOpen() ? this.iconSanitizer.getIcon('arrowUp') : this.iconSanitizer.getIcon('arrowDown')
    );

    openCloseStatusList(event: Event): void {
        event.stopPropagation();
        if (!this.hasAnyOptions()) return;

        if (!this.isOverlayGroupsOpen()) {
            const triggerWidth = this.trigger?.elementRef.nativeElement.getBoundingClientRect().width;

            if (triggerWidth) {
                this.overlayMinWidth.set(triggerWidth);
            }
        }

        this.isOverlayGroupsOpen.update((value) => !value);
    }

    onBidStatusSelected(status: OpportunityStatusAvailableDto): void {
        this.changeStatusEvent.emit(status);
        this.isOverlayGroupsOpen.set(false);
    }

    onDisqualifyToggleClick(): void {
        this.disqualifyToggleClick.emit();
        this.isOverlayGroupsOpen.set(false);
    }

    onManualStatusClick(): void {
        this.manualStatusClick.emit();
        this.isOverlayGroupsOpen.set(false);
    }
}
